import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase";
import logo from "../assets/image/logo_web.png";
import cogzImage from "../assets/cogz.png";
import { getCurrentUserCompanyId, getCurrentUserData } from "../utils/userUtils";
import { hasPermission } from "../utils/permissions";

const DEFAULT_ASSIGNMENT = {
  allUsers: true,
  userIds: [],
  roles: [],
  groups: [],
};

function toDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString("en-GB") : "Not set";
}

function formatDateTime(value) {
  const date = toDate(value);
  return date ? date.toLocaleString("en-GB") : "Not recorded";
}

function formatDetailValue(value, users = [], key = "") {
  if (["userId", "actorId", "uploadedBy", "updatedBy", "createdBy"].includes(key) && typeof value === "string") {
    return getUserName(value, users);
  }
  if (Array.isArray(value)) return value.map((item) => formatDetailValue(item, users, key)).join(", ");
  if (value && typeof value === "object") {
    return Object.entries(value).map(([childKey, item]) => `${childKey}: ${formatDetailValue(item, users, childKey)}`).join("; ");
  }
  return String(value ?? "Not recorded");
}

function getUserGroups(user) {
  return Array.isArray(user?.groups) ? user.groups : user?.group ? [user.group] : [];
}

function getAssignedUsersForPolicy(policy, users = []) {
  const assignment = policy.assignment || DEFAULT_ASSIGNMENT;
  if (assignment.allUsers !== false) return users;

  return users.filter((user) => {
    const role = String(user.role || "").toLowerCase();
    const userGroups = getUserGroups(user);
    return (
      assignment.userIds?.includes(user.id) ||
      assignment.roles?.map((r) => String(r).toLowerCase()).includes(role) ||
      userGroups.some((group) => assignment.groups?.includes(group))
    );
  });
}

function getCurrentAcknowledgement(policy, acknowledgements, userId) {
  const ack = acknowledgements.find(
    (item) =>
      item.policyId === policy.id &&
      item.userId === userId &&
      Number(item.version || 1) === Number(policy.currentVersion || 1)
  );
  if (!ack) return null;

  const signedAt = toDate(ack.signedAt || ack.readConfirmedAt);
  const reviewIntervalDays = Number(policy.reviewIntervalDays || 365);
  if (!signedAt || !Number.isFinite(reviewIntervalDays) || reviewIntervalDays <= 0) {
    return ack;
  }

  const dueAt = new Date(signedAt);
  dueAt.setDate(dueAt.getDate() + reviewIntervalDays);
  return dueAt > new Date() ? ack : null;
}

function lifecycleStatus(policy) {
  const expiry = toDate(policy.expiryDate);
  if (expiry && expiry < new Date()) return "expired";
  return policy.status || "active";
}

function complianceStatus(policy, users, acknowledgements) {
  const assigned = getAssignedUsersForPolicy(policy, users);
  const completed = assigned.filter((user) => getCurrentAcknowledgement(policy, acknowledgements, user.id)).length;
  const pending = Math.max(assigned.length - completed, 0);
  const expired = lifecycleStatus(policy) === "expired";

  if (expired || pending > 0) {
    return {
      key: expired ? "overdue" : "pending",
      label: expired ? "Overdue" : "Pending",
      completed,
      pending,
      total: assigned.length,
      percent: assigned.length ? Math.round((completed / assigned.length) * 100) : 100,
    };
  }

  return {
    key: "completed",
    label: "Completed",
    completed,
    pending,
    total: assigned.length,
    percent: 100,
  };
}

function assignmentSummary(policy, users, groups) {
  const assignment = policy.assignment || DEFAULT_ASSIGNMENT;
  if (assignment.allUsers !== false) return "All users";

  const names = [
    ...(assignment.userIds || []).map((id) => getUserName(id, users)),
    ...(assignment.roles || []).map((role) => `Role: ${role}`),
    ...(assignment.groups || []).map((id) => `Group: ${groups.find((group) => group.id === id)?.name || "Unknown group"}`),
  ];

  return names.length ? names.join("; ") : "No assignment";
}

function getUserName(userOrId, users = []) {
  const user = typeof userOrId === "string" ? users.find((item) => item.id === userOrId) : userOrId;
  if (!user) return typeof userOrId === "string" && userOrId.includes("@") ? userOrId : "Unknown user";
  return user.fullName || user.displayName || user.email || "Unknown user";
}

function getAcknowledgementUserName(ack, users = []) {
  return ack?.userName || ack?.userEmail || getUserName(ack?.userId, users);
}

function getActivityActorName(activity, users = []) {
  return activity?.actorEmail || getUserName(activity?.actorId, users);
}

function getVersionRecord(policy, versionNumber = policy?.currentVersion || 1) {
  const versions = Array.isArray(policy?.versions) ? policy.versions : [];
  return versions.find((version) => Number(version.version) === Number(versionNumber)) || policy?.currentFile || null;
}

function getVersionDate(policy, versionNumber = policy?.currentVersion || 1) {
  const record = getVersionRecord(policy, versionNumber);
  return record?.uploadedAt || policy?.updatedAt || policy?.createdAt;
}

function getAssessmentStatus(ack) {
  if (!ack?.assessment?.required) return "Not required";
  if (ack.assessment.passed == null) return "Not scored";
  return ack.assessment.passed ? "Passed" : "Failed";
}

function getTrainingRowStatus(policy, ack) {
  if (ack?.assessment?.required && ack.assessment?.passed === false) return "Failed";
  if (ack) return "Completed";
  return lifecycleStatus(policy) === "expired" ? "Overdue" : "Pending";
}

function answerStatus(question, ack) {
  const answer = answerFor(question, ack);
  if (!String(answer ?? "").trim()) return { label: "No answer", ok: false };
  if (question.type === "short_answer") return { label: "Manual review", ok: true };
  const ok = String(answer).trim().toLowerCase() === String(question.correctAnswer || "").trim().toLowerCase();
  return { label: ok ? "Correct" : "Incorrect", ok };
}

function csvEscape(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function downloadCsv(filename, headers, rows) {
  const csv = [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => csvEscape(row[header])).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function answerFor(question, ack) {
  return ack?.assessment?.answers?.[question.id] ?? "";
}

export default function KnowledgeHub() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [acknowledgements, setAcknowledgements] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [selectedAck, setSelectedAck] = useState(null);
  const [knowledgeQuestion, setKnowledgeQuestion] = useState("");
  const [knowledgeAnswer, setKnowledgeAnswer] = useState(null);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeError, setKnowledgeError] = useState("");

  const canManage = hasPermission(userProfile, "manage_policies");
  const currentUserId = auth.currentUser?.uid;

  useEffect(() => {
    let unsubscribe = null;
    let mounted = true;

    async function load() {
      const [cid, profile] = await Promise.all([
        getCurrentUserCompanyId(),
        getCurrentUserData(),
      ]);
      if (!mounted) return;

      setCompanyId(cid);
      setUserProfile({ ...profile, uid: auth.currentUser?.uid });

      if (!cid) {
        setLoading(false);
        return;
      }

      const [usersSnap, groupsSnap] = await Promise.all([
        getDocs(query(collection(db, "users"))),
        getDocs(collection(db, "companies", cid, "groups")),
      ]);

      if (!mounted) return;
      setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((user) => user.companyId === cid));
      setGroups(groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      unsubscribe = onSnapshot(
        query(collection(db, "companies", cid, "policies"), orderBy("updatedAt", "desc")),
        async (snapshot) => {
          const policyRows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          const ackRows = [];
          const activities = [];

          for (const policy of policyRows) {
            const [ackSnap, activitySnap] = await Promise.all([
              getDocs(collection(db, "companies", cid, "policies", policy.id, "acknowledgements")),
              getDocs(query(collection(db, "companies", cid, "policies", policy.id, "activity"), orderBy("createdAt", "desc"))),
            ]);

            ackSnap.forEach((ackDoc) => ackRows.push({ id: ackDoc.id, policyId: policy.id, ...ackDoc.data() }));
            activitySnap.forEach((activityDoc) => activities.push({ id: activityDoc.id, policyId: policy.id, policyTitle: policy.title, ...activityDoc.data() }));
          }

          if (!mounted) return;
          setPolicies(policyRows);
          setAcknowledgements(ackRows);
          setActivityRows(activities.sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)));
          setSelectedPolicyId((current) => current || policyRows[0]?.id || null);
          setLoading(false);
        },
        (error) => {
          console.error("Knowledge Hub load failed:", error);
          setLoading(false);
        }
      );
    }

    load();
    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const visiblePolicies = useMemo(
    () => canManage
      ? policies
      : policies.filter((policy) => getAssignedUsersForPolicy(policy, users).some((user) => user.id === currentUserId)),
    [canManage, policies, users, currentUserId]
  );

  const visibleActivities = useMemo(() => {
    const ids = new Set(visiblePolicies.map((policy) => policy.id));
    return activityRows.filter((row) => ids.has(row.policyId));
  }, [activityRows, visiblePolicies]);

  const categoryOptions = useMemo(
    () => ["all", ...Array.from(new Set(visiblePolicies.map((policy) => policy.category || "General"))).sort()],
    [visiblePolicies]
  );

  const roleOptions = useMemo(
    () => ["all", ...Array.from(new Set(users.map((user) => user.role).filter(Boolean))).sort()],
    [users]
  );

  const groupOptions = useMemo(
    () => ["all", ...groups.map((group) => group.id)],
    [groups]
  );

  useEffect(() => {
    if (visiblePolicies.length === 0) {
      setSelectedPolicyId(null);
      return;
    }
    if (!visiblePolicies.some((policy) => policy.id === selectedPolicyId)) {
      setSelectedPolicyId(visiblePolicies[0].id);
    }
  }, [visiblePolicies, selectedPolicyId]);

  const documentRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visiblePolicies
      .map((policy) => {
        const compliance = complianceStatus(policy, users, acknowledgements);
        return {
          policy,
          compliance,
          assignment: assignmentSummary(policy, users, groups),
        };
      })
      .filter(({ policy, compliance, assignment }) => {
        const matchesSearch = !q || [
          policy.title,
          policy.description,
          policy.category,
          assignment,
          ...(policy.tags || []),
        ].some((value) => String(value || "").toLowerCase().includes(q));
        const matchesStatus = statusFilter === "all" || compliance.key === statusFilter || lifecycleStatus(policy) === statusFilter;
        const matchesCategory = categoryFilter === "all" || (policy.category || "General") === categoryFilter;
        return matchesSearch && matchesStatus && matchesCategory;
      });
  }, [visiblePolicies, users, acknowledgements, groups, search, statusFilter, categoryFilter]);

  const trainingRows = useMemo(() => {
    return visiblePolicies.flatMap((policy) => {
      const assignedUsers = getAssignedUsersForPolicy(policy, users).filter((user) => canManage || user.id === currentUserId);
      return assignedUsers.map((user) => {
        const ack = getCurrentAcknowledgement(policy, acknowledgements, user.id);
        return {
          id: `${policy.id}_${user.id}`,
          policy,
          user,
          ack,
          status: getTrainingRowStatus(policy, ack),
          score: ack?.assessment?.score,
          passed: ack?.assessment?.passed,
        };
      });
    });
  }, [visiblePolicies, users, acknowledgements, canManage, currentUserId]);

  const filteredTrainingRows = useMemo(() => {
    return trainingRows.filter((row) => {
      const roleMatches = roleFilter === "all" || row.user.role === roleFilter;
      const groupMatches = groupFilter === "all" || getUserGroups(row.user).includes(groupFilter);
      return roleMatches && groupMatches;
    });
  }, [trainingRows, roleFilter, groupFilter]);

  const kpis = useMemo(() => {
    const completed = filteredTrainingRows.filter((row) => row.status === "Completed").length;
    const overdue = filteredTrainingRows.filter((row) => row.status === "Overdue").length;
    const visiblePolicyIds = new Set(visiblePolicies.map((policy) => policy.id));
    const failed = acknowledgements.filter((ack) => visiblePolicyIds.has(ack.policyId) && ack.assessment?.required && ack.assessment?.passed === false).length;
    const completion = filteredTrainingRows.length ? Math.round((completed / filteredTrainingRows.length) * 100) : 100;
    const expiring = visiblePolicies.filter((policy) => {
      const expiry = toDate(policy.expiryDate);
      if (!expiry) return false;
      const soon = new Date();
      soon.setDate(soon.getDate() + 30);
      return expiry >= new Date() && expiry <= soon;
    }).length;

    return { completed, overdue, failed, completion, expiring };
  }, [filteredTrainingRows, acknowledgements, visiblePolicies]);

  const selectedPolicy = visiblePolicies.find((policy) => policy.id === selectedPolicyId) || null;
  const selectedPolicyAcks = acknowledgements.filter((ack) => ack.policyId === selectedPolicy?.id && (canManage || ack.userId === currentUserId));
  const selectedPolicyActivity = visibleActivities.filter((row) => row.policyId === selectedPolicy?.id);
  const failedQuestions = useMemo(() => {
    const counts = new Map();
    acknowledgements.forEach((ack) => {
      const policy = visiblePolicies.find((item) => item.id === ack.policyId);
      if (!policy) return;
      (policy?.assessment?.questions || []).forEach((question) => {
        const answer = answerFor(question, ack);
        if (question.type === "short_answer" || !answer) return;
        if (String(answer).trim().toLowerCase() !== String(question.correctAnswer || "").trim().toLowerCase()) {
          const existing = counts.get(question.id) || { question: question.question, policyTitle: policy.title, count: 0 };
          counts.set(question.id, { ...existing, count: existing.count + 1 });
        }
      });
    });
    return Array.from(counts.values()).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [acknowledgements, visiblePolicies]);

  const suggestedDocuments = useMemo(() => {
    const userRole = String(userProfile?.role || "").toLowerCase();
    const userGroups = getUserGroups(userProfile);

    return visiblePolicies
      .map((policy) => {
        const assignment = policy.assignment || DEFAULT_ASSIGNMENT;
        const hasSummary = !!String(policy.assessment?.aiDocumentSummary || "").trim();
        const roleMatch = assignment.roles?.map((role) => String(role).toLowerCase()).includes(userRole);
        const groupMatch = userGroups.some((group) => assignment.groups?.includes(group));
        const pendingRow = trainingRows.find((row) => row.policy.id === policy.id && row.user.id === currentUserId && row.status !== "Completed");
        const score = (pendingRow ? 4 : 0) + (roleMatch ? 3 : 0) + (groupMatch ? 2 : 0) + (hasSummary ? 1 : 0);
        return { policy, hasSummary, score, reason: pendingRow ? "Requires your attention" : roleMatch ? "Relevant to your role" : groupMatch ? "Relevant to your group" : hasSummary ? "Available for AI answers" : "Needs summary" };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [visiblePolicies, userProfile, trainingRows, currentUserId]);

  const askKnowledgeHub = async (event) => {
    event.preventDefault();
    const question = knowledgeQuestion.trim();
    if (!question) return;

    setKnowledgeLoading(true);
    setKnowledgeError("");
    setKnowledgeAnswer(null);

    try {
      const ask = httpsCallable(functions, "askKnowledgeHub");
      const result = await ask({ companyId, question });
      setKnowledgeAnswer(result.data);
    } catch (error) {
      console.error("Knowledge Hub AI failed:", error);
      setKnowledgeError(error.message || t("knowledgeHub.ai.error", "Could not answer from the Knowledge Hub."));
    } finally {
      setKnowledgeLoading(false);
    }
  };

  const exportTrainingRecords = () => {
    const headers = ["User", "Email", "Role", "Groups", "Document", "Category", "Version", "Version Date", "Status", "Assessment Required", "Pass Percentage", "Assessment Passed", "Signed At", "Signature Captured", "IP", "Device"];
    const rows = filteredTrainingRows.map((row) => ({
      User: getUserName(row.user, users),
      Email: row.user.email || "",
      Role: row.user.role || "",
      Groups: getUserGroups(row.user).join("; "),
      Document: row.policy.title,
      Category: row.policy.category || "General",
      Version: row.policy.currentVersion || 1,
      "Version Date": formatDate(getVersionDate(row.policy, row.ack?.version || row.policy.currentVersion || 1)),
      Status: row.status,
      "Assessment Required": row.policy.assessment?.enabled ? "Yes" : "No",
      "Pass Percentage": row.score != null ? `${row.score}%` : "N/A",
      "Assessment Passed": getAssessmentStatus(row.ack),
      "Signed At": row.ack ? formatDateTime(row.ack.signedAt) : "",
      "Signature Captured": row.ack?.signature ? "Yes" : "No",
      IP: row.ack?.ipAddress || "Not captured",
      Device: row.ack?.device || row.ack?.userAgent || "",
    }));
    downloadCsv(`knowledge-hub-training-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const exportDocumentIndex = () => {
    const headers = ["Document", "Category", "Version", "Version Date", "Author / Uploader", "Created", "Updated", "Review Days", "Expiry", "Assignment", "Compliance", "Completed", "Pending", "Tags", "Linked Checks"];
    const rows = documentRows.map(({ policy, compliance, assignment }) => ({
      Document: policy.title,
      Category: policy.category || "General",
      Version: policy.currentVersion || 1,
      "Version Date": formatDate(getVersionDate(policy)),
      "Author / Uploader": getUserName(policy.currentFile?.uploadedBy || policy.createdBy || policy.updatedBy, users),
      Created: formatDate(policy.createdAt),
      Updated: formatDate(policy.updatedAt),
      "Review Days": policy.reviewIntervalDays || 365,
      Expiry: formatDate(policy.expiryDate),
      Assignment: assignment,
      Compliance: compliance.label,
      Completed: compliance.completed,
      Pending: compliance.pending,
      Tags: (policy.tags || []).join("; "),
      "Linked Checks": (policy.linkedChecks || []).join("; "),
    }));
    downloadCsv(`knowledge-hub-documents-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white p-8">{t("knowledgeHub.loading", "Loading Knowledge Hub...")}</div>;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,#0f766e33,transparent_34%),linear-gradient(135deg,#020617,#0f172a_55%,#111827)] text-white px-6 py-8">
      <header className="sticky top-0 z-40 -mx-6 -mt-8 mb-8 px-6 py-4 bg-slate-950/85 backdrop-blur-xl border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
              <img src={logo} alt="BuildMyChecks" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-teal-300">{t("knowledgeHub.eyebrow", "BuildMyChecks Intelligence Centre")}</p>
              <h1 className="text-2xl font-black">{t("knowledgeHub.title", "Knowledge Hub")}</h1>
            </div>
          </div>
          <nav className="flex flex-wrap gap-2">
            <button onClick={() => navigate("/home")} className="px-3 py-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/15">{t("knowledgeHub.nav.home", "Home")}</button>
            <button onClick={() => navigate("/dashboard")} className="px-3 py-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/15">{t("knowledgeHub.nav.dashboard", "Dashboard")}</button>
            <button onClick={() => navigate("/policies")} className="px-3 py-2 rounded-full bg-teal-400 text-slate-950 font-bold">{t("knowledgeHub.nav.managePolicies", "Manage Policies")}</button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto space-y-8">
        <section className="grid lg:grid-cols-5 gap-4">
          {[
            [t("knowledgeHub.kpis.documents", "Documents"), visiblePolicies.length],
            [t("knowledgeHub.kpis.completion", "Completion"), `${kpis.completion}%`],
            [t("knowledgeHub.kpis.overdue", "Overdue"), kpis.overdue],
            [t("knowledgeHub.kpis.failed", "Failed Assessments"), kpis.failed],
            [t("knowledgeHub.kpis.expiring", "Expiring Soon"), kpis.expiring],
          ].map(([label, value]) => (
            <div key={label} className="rounded-3xl bg-white/10 border border-white/10 p-5">
              <div className="text-3xl font-black">{value}</div>
              <div className="text-sm text-slate-300">{label}</div>
            </div>
          ))}
        </section>

        <section className="grid xl:grid-cols-[1.3fr_0.7fr] gap-6">
          <div className="rounded-3xl bg-gradient-to-br from-cyan-400/15 to-teal-500/10 border border-cyan-200/20 p-5">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="cogz-assistant-orb" aria-hidden="true">
                  <img src={cogzImage} alt="" />
                </div>
                <div>
                <p className="uppercase tracking-[0.2em] text-cyan-200 text-xs">{t("knowledgeHub.ai.eyebrow", "AI-Powered Knowledge Assistance")}</p>
                <h2 className="text-2xl font-black mt-1">{t("knowledgeHub.ai.cogzTitle", "Ask Cogz about your controlled documents")}</h2>
                <p className="text-sm text-slate-300 mt-2">
                  {t("knowledgeHub.ai.subtitle", "Answers use generated policy summaries only, so large PDFs are not rescanned for every question.")}
                </p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full bg-slate-950/60 border border-white/10 text-xs text-cyan-100 self-start">
                {t("knowledgeHub.ai.summaryBased", "Summary-based search")}
              </span>
            </div>

            <form onSubmit={askKnowledgeHub} className="mt-5 space-y-3">
              <textarea
                value={knowledgeQuestion}
                onChange={(event) => setKnowledgeQuestion(event.target.value)}
                className="w-full min-h-28 rounded-2xl bg-slate-950/75 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500"
                placeholder={t("knowledgeHub.ai.placeholder", 'Ask a question, for example "What PPE is required before warehouse work?"')}
              />
              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={knowledgeLoading || !knowledgeQuestion.trim()}
                  className="px-5 py-3 rounded-2xl bg-cyan-300 text-slate-950 font-black disabled:opacity-50"
                >
                  {knowledgeLoading ? t("knowledgeHub.ai.searching", "Searching summaries...") : t("knowledgeHub.ai.askButton", "Ask Knowledge Hub")}
                </button>
                <button
                  type="button"
                  onClick={() => setKnowledgeQuestion(t("knowledgeHub.ai.exampleQuestion", "Which policies are overdue or need my attention?"))}
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/10 font-bold text-sm"
                >
                  {t("knowledgeHub.ai.exampleButton", "Try attention query")}
                </button>
              </div>
            </form>

            {knowledgeError && (
              <div className="mt-4 rounded-2xl bg-red-400/15 border border-red-300/25 p-4 text-sm text-red-100">
                {knowledgeError}
              </div>
            )}

            {knowledgeAnswer && (
              <div className="mt-5 rounded-2xl bg-slate-950/70 border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <img src={cogzImage} alt="" className="w-12 h-12 rounded-2xl object-cover border border-cyan-200/30 bg-white/90" />
                  <h3 className="font-black">{t("knowledgeHub.ai.cogzAnswer", "Cogz says")}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-200 whitespace-pre-wrap">{knowledgeAnswer.answer}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(knowledgeAnswer.sources || []).map((source) => (
                    <button
                      key={source.policyId}
                      type="button"
                      onClick={() => setSelectedPolicyId(source.policyId)}
                      className="px-3 py-2 rounded-xl bg-cyan-300/10 border border-cyan-200/20 text-cyan-100 text-xs font-bold"
                    >
                      {source.title} v{source.version}
                    </button>
                  ))}
                </div>
                {knowledgeAnswer.model && <p className="mt-3 text-xs text-slate-500">{t("knowledgeHub.ai.generatedWith", "Generated with {{model}}", { model: knowledgeAnswer.model })}</p>}
              </div>
            )}
          </div>

          <aside className="rounded-3xl bg-white/10 border border-white/10 p-5">
            <h2 className="text-xl font-black">{t("knowledgeHub.suggestions.title", "Suggested documents")}</h2>
            <p className="text-sm text-slate-300 mt-1">{t("knowledgeHub.suggestions.subtitle", "Based on your role, groups, pending sign-offs, and available summaries.")}</p>
            <div className="mt-4 space-y-3">
              {suggestedDocuments.length === 0 ? (
                <p className="text-sm text-slate-400">{t("knowledgeHub.suggestions.empty", "No suggestions yet. Generate policy summaries to unlock AI assistance.")}</p>
              ) : suggestedDocuments.map(({ policy, reason, hasSummary }) => (
                <button
                  key={policy.id}
                  type="button"
                  onClick={() => setSelectedPolicyId(policy.id)}
                  className="w-full text-left rounded-2xl bg-slate-950/50 border border-white/10 p-3 hover:border-cyan-200/40 transition"
                >
                  <div className="font-bold">{policy.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{reason} - {hasSummary ? t("knowledgeHub.suggestions.summaryReady", "AI summary ready") : t("knowledgeHub.suggestions.summaryNeeded", "Summary needed")}</div>
                </button>
              ))}
            </div>
          </aside>
        </section>

        <section className="rounded-3xl bg-white/10 border border-white/10 p-5 grid lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Search</label>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Keyword, category, user, tag, assignment..." className="mt-2 w-full rounded-2xl bg-slate-950/70 border border-white/10 px-4 py-3" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Status</label>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="mt-2 w-full rounded-2xl bg-slate-950/70 border border-white/10 px-4 py-3">
              <option value="all">All statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Category</label>
            <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} className="mt-2 w-full rounded-2xl bg-slate-950/70 border border-white/10 px-4 py-3">
              {categoryOptions.map((category) => <option key={category} value={category}>{category === "all" ? "All categories" : category}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Training filter</label>
            <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} className="mt-2 w-full rounded-2xl bg-slate-950/70 border border-white/10 px-4 py-3">
              {roleOptions.map((role) => <option key={role} value={role}>{role === "all" ? "All roles" : role}</option>)}
            </select>
          </div>
          <div className="flex gap-2 items-end lg:col-span-6 xl:col-span-1">
            <button onClick={exportDocumentIndex} className="flex-1 px-3 py-3 rounded-2xl bg-white text-slate-950 font-bold">Export Docs</button>
            <button onClick={exportTrainingRecords} className="flex-1 px-3 py-3 rounded-2xl bg-emerald-400 text-slate-950 font-bold">Export Records</button>
          </div>
          {canManage && groups.length > 0 && (
            <div className="lg:col-span-6">
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Group / department</label>
              <select value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)} className="mt-2 w-full rounded-2xl bg-slate-950/70 border border-white/10 px-4 py-3">
                {groupOptions.map((groupId) => (
                  <option key={groupId} value={groupId}>
                    {groupId === "all" ? "All groups" : groups.find((group) => group.id === groupId)?.name || "Unknown group"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </section>

        <section className="grid xl:grid-cols-[1.35fr_0.9fr] gap-6">
          <div className="rounded-3xl bg-white/10 border border-white/10 overflow-hidden">
            <div className="p-5 border-b border-white/10">
              <h2 className="text-2xl font-black">Document Control Index</h2>
              <p className="text-sm text-slate-300">Structured lifecycle, assignment, and compliance view for all controlled documents.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/70 text-slate-300">
                  <tr>
                    {["Document", "Version", "Review / Expiry", "Assignment", "Compliance"].map((head) => (
                      <th key={head} className="text-left px-4 py-3 font-bold">{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {documentRows.map(({ policy, compliance, assignment }) => (
                    <tr key={policy.id} onClick={() => setSelectedPolicyId(policy.id)} className={`cursor-pointer border-t border-white/10 hover:bg-white/10 ${selectedPolicyId === policy.id ? "bg-teal-400/10" : ""}`}>
                      <td className="px-4 py-3">
                        <div className="font-black">{policy.title}</div>
                        <div className="text-xs text-slate-400">{policy.category || "General"} - Created {formatDate(policy.createdAt)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>v{policy.currentVersion || 1}</div>
                        <div className="text-xs text-slate-400">{(policy.versions || []).length} revision(s)</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{policy.reviewIntervalDays || 365} day review</div>
                        <div className="text-xs text-slate-400">Expires {formatDate(policy.expiryDate)}</div>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="line-clamp-2">{assignment}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${compliance.key === "completed" ? "bg-emerald-400/20 text-emerald-100" : compliance.key === "pending" ? "bg-amber-400/20 text-amber-100" : "bg-red-400/20 text-red-100"}`}>
                          {compliance.label} - {compliance.percent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  {documentRows.length === 0 && (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">No documents match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-3xl bg-white/10 border border-white/10 p-5">
              <h2 className="text-xl font-black">Smart Insights</h2>
              <div className="mt-4 space-y-3">
                {failedQuestions.length === 0 ? (
                  <p className="text-sm text-slate-300">No repeated assessment failures detected yet.</p>
                ) : failedQuestions.map((item) => (
                  <div key={`${item.policyTitle}_${item.question}`} className="rounded-2xl bg-red-400/10 border border-red-300/20 p-3">
                    <div className="font-bold text-red-100">{item.count} failed answer(s)</div>
                    <div className="text-sm text-slate-300 mt-1">{item.question}</div>
                    <div className="text-xs text-slate-400 mt-1">{item.policyTitle}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl bg-white/10 border border-white/10 p-5">
              <h2 className="text-xl font-black">System Audit Log</h2>
              <div className="mt-4 space-y-3 max-h-80 overflow-y-auto pr-1">
                {visibleActivities.slice(0, 12).map((activity) => (
                  <div key={`${activity.policyId}_${activity.id}`} className="rounded-2xl bg-slate-950/50 border border-white/10 p-3">
                    <div className="font-bold">{activity.type?.replace(/_/g, " ") || "Activity"}</div>
                    <div className="text-sm text-slate-300">{activity.policyTitle}</div>
                    <div className="text-xs text-slate-400">{formatDateTime(activity.createdAt)} - {getActivityActorName(activity, users)}</div>
                  </div>
                ))}
                {visibleActivities.length === 0 && <p className="text-sm text-slate-300">No activity recorded yet.</p>}
              </div>
            </div>
          </aside>
        </section>

        {selectedPolicy && (
          <section className="rounded-3xl bg-white/10 border border-white/10 p-5">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <p className="uppercase tracking-[0.2em] text-teal-300 text-xs">Document Drill-Down</p>
                <h2 className="text-3xl font-black mt-1">{selectedPolicy.title}</h2>
                <p className="text-slate-300 mt-2">{selectedPolicy.description || "No description provided."}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">Category: {selectedPolicy.category || "General"}</span>
                  <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">Current version: v{selectedPolicy.currentVersion || 1}</span>
                  <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">Version date: {formatDate(getVersionDate(selectedPolicy))}</span>
                  <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">Uploader: {getUserName(selectedPolicy.currentFile?.uploadedBy || selectedPolicy.createdBy, users)}</span>
                  <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">Assignment: {assignmentSummary(selectedPolicy, users, groups)}</span>
                </div>
              </div>
              <a href={selectedPolicy.currentFile?.downloadURL} target="_blank" rel="noreferrer" className="px-4 py-3 rounded-2xl bg-white text-slate-950 font-bold text-center">
                Open Current Version
              </a>
            </div>

            <div className="grid xl:grid-cols-3 gap-5 mt-6">
              <div className="rounded-2xl bg-slate-950/50 border border-white/10 p-4">
                <h3 className="font-black">Lifecycle</h3>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-xl bg-white/5 p-3">
                    <div className="text-xs text-slate-400">Created</div>
                    <div className="font-bold">{formatDate(selectedPolicy.createdAt)}</div>
                  </div>
                  <div className="rounded-xl bg-white/5 p-3">
                    <div className="text-xs text-slate-400">Last updated</div>
                    <div className="font-bold">{formatDate(selectedPolicy.updatedAt)}</div>
                  </div>
                  <div className="rounded-xl bg-white/5 p-3">
                    <div className="text-xs text-slate-400">Review</div>
                    <div className="font-bold">{selectedPolicy.reviewIntervalDays || 365} days</div>
                  </div>
                  <div className="rounded-xl bg-white/5 p-3">
                    <div className="text-xs text-slate-400">Expiry</div>
                    <div className="font-bold">{formatDate(selectedPolicy.expiryDate)}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-950/50 border border-white/10 p-4">
                <h3 className="font-black">Revision History</h3>
                <div className="mt-3 space-y-2">
                  {(selectedPolicy.versions || []).slice().reverse().map((version) => (
                    <div key={version.version} className="flex justify-between gap-3 border-t border-white/10 pt-2 text-sm">
                      <span>v{version.version} - {version.fileName}</span>
                      <span className="text-slate-400">{formatDate(version.uploadedAt)} - {getUserName(version.uploadedBy, users)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-950/50 border border-white/10 p-4">
                <h3 className="font-black">Linked Records</h3>
                <div className="mt-3 space-y-2 text-sm">
                  {(selectedPolicy.linkedChecks || []).length === 0 ? (
                    <p className="text-slate-400">No linked checks, incidents, or risk assessments recorded yet.</p>
                  ) : (
                    selectedPolicy.linkedChecks.map((item) => (
                      <div key={item} className="rounded-xl bg-white/5 border border-white/10 p-3">{item}</div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-950/50 border border-white/10 p-4 xl:col-span-3">
                <h3 className="font-black">Assessment & Sign-Off Submissions</h3>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-slate-400">
                      <tr>
                        {["User", "Score", "Passed", "Signed", "Signature", "Device"].map((head) => <th key={head} className="text-left py-2 pr-3">{head}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPolicyAcks.map((ack) => (
                        <tr key={ack.id} className="border-t border-white/10">
                          <td className="py-2 pr-3">{getAcknowledgementUserName(ack, users)}</td>
                          <td className="py-2 pr-3">{ack.assessment?.score != null ? `${ack.assessment.score}%` : "N/A"}</td>
                          <td className="py-2 pr-3">{ack.assessment?.passed == null ? "N/A" : ack.assessment.passed ? "Yes" : "No"}</td>
                          <td className="py-2 pr-3">{formatDateTime(ack.signedAt)}</td>
                          <td className="py-2 pr-3">{ack.signature ? "Captured" : "Missing"}</td>
                          <td className="py-2 pr-3">{ack.device || ack.userAgent || "Unknown"}</td>
                          <td className="py-2 pr-3">
                            <button onClick={() => setSelectedAck(ack)} className="px-3 py-1 rounded-lg bg-teal-400 text-slate-950 font-bold">View</button>
                          </td>
                        </tr>
                      ))}
                      {selectedPolicyAcks.length === 0 && (
                        <tr><td colSpan="7" className="py-6 text-center text-slate-400">No submissions yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-slate-950/50 border border-white/10 p-4 mt-5">
              <h3 className="font-black">User & Group Training Records</h3>
              <div className="mt-3 grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {filteredTrainingRows.filter((row) => row.policy.id === selectedPolicy.id).map((row) => (
                  <div key={row.id} className="rounded-2xl bg-white/5 border border-white/10 p-3">
                    <div className="font-bold">{getUserName(row.user, users)}</div>
                    <div className="text-xs text-slate-400">{row.user.role || "user"} - {getUserGroups(row.user).join(", ") || "No group"}</div>
                    <div className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold ${row.status === "Completed" ? "bg-emerald-400/20 text-emerald-100" : row.status === "Overdue" ? "bg-red-400/20 text-red-100" : "bg-amber-400/20 text-amber-100"}`}>
                      {row.status}{row.score != null ? ` - ${row.score}%` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-slate-950/50 border border-white/10 p-4 mt-5">
              <h3 className="font-black">Document Activity Trail</h3>
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                {selectedPolicyActivity.map((activity) => (
                  <div key={`${activity.policyId}_${activity.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-3">
                    <div className="font-bold capitalize">{String(activity.type || "activity").replace(/_/g, " ")}</div>
                    <div className="text-xs text-slate-400">{formatDateTime(activity.createdAt)} - {getActivityActorName(activity, users)}</div>
                    {activity.details && typeof activity.details === "object" && (
                      <dl className="mt-2 grid gap-1 text-xs text-slate-300">
                        {Object.entries(activity.details).map(([key, value]) => (
                          <div key={key} className="flex justify-between gap-3 rounded-lg bg-black/20 px-2 py-1">
                            <dt className="capitalize text-slate-400">{key.replace(/([A-Z])/g, " $1")}</dt>
                            <dd className="text-right">{formatDetailValue(value, users, key)}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                ))}
                {selectedPolicyActivity.length === 0 && <p className="text-sm text-slate-400">No activity recorded for this document yet.</p>}
              </div>
            </div>
          </section>
        )}
      </main>

      {selectedAck && selectedPolicy && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white text-slate-950 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black">Assessment Submission</h2>
                <p className="text-slate-600">{getAcknowledgementUserName(selectedAck, users)} - {formatDateTime(selectedAck.signedAt)}</p>
              </div>
              <button onClick={() => setSelectedAck(null)} className="font-bold text-slate-500">Close</button>
            </div>

            <div className="grid md:grid-cols-4 gap-3 my-5">
              <div className="rounded-2xl bg-slate-100 p-3"><div className="text-xs text-slate-500">Score</div><div className="font-black">{selectedAck.assessment?.score != null ? `${selectedAck.assessment.score}%` : "N/A"}</div></div>
              <div className="rounded-2xl bg-slate-100 p-3"><div className="text-xs text-slate-500">Passed</div><div className="font-black">{selectedAck.assessment?.passed == null ? "N/A" : selectedAck.assessment.passed ? "Yes" : "No"}</div></div>
              <div className="rounded-2xl bg-slate-100 p-3"><div className="text-xs text-slate-500">Device</div><div className="font-black truncate">{selectedAck.device || selectedAck.userAgent || "Unknown"}</div></div>
              <div className="rounded-2xl bg-slate-100 p-3"><div className="text-xs text-slate-500">IP</div><div className="font-black">{selectedAck.ipAddress || "Not captured"}</div></div>
            </div>

            <div className="space-y-3">
              {(selectedPolicy.assessment?.questions || []).map((question, index) => {
                const answer = answerFor(question, selectedAck);
                const status = answerStatus(question, selectedAck);
                return (
                  <div key={question.id} className={`rounded-2xl border p-4 ${status.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
                    <div className="flex flex-wrap justify-between gap-2">
                      <div className="font-black">{index + 1}. {question.question}</div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${status.ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{status.label}</span>
                    </div>
                    <div className="mt-2 text-sm"><span className="font-bold">User answer:</span> {String(answer || "No answer")}</div>
                    <div className="mt-1 text-sm"><span className="font-bold">Correct / marking guide:</span> {question.type === "short_answer" ? (question.correctAnswer || "Manual review") : question.correctAnswer}</div>
                  </div>
                );
              })}
            </div>

            {selectedAck.signature && (
              <div className="mt-5 rounded-2xl border border-slate-200 p-4">
                <h3 className="font-black mb-2">Digital Signature</h3>
                <img src={selectedAck.signature} alt="User signature" className="max-h-40 border rounded-xl bg-white" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
